import { ApplicationConfig, Injector, inject } from '@angular/core';
import { ApolloClientOptions, ApolloLink, FetchResult, InMemoryCache } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { fragmentRegistry } from './graphql/fragmentRegistry';
import { EnvironmentService } from './services/environment.service';
import { SystemLockService } from './services/system-lock.service';

// Recursively parse LocalDate strings to Date objects
function parseLocalDates(obj: any): void {
  if (!obj || typeof obj !== 'object') return;

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach(item => parseLocalDates(item));
    return;
  }

  // Handle objects - look for fields named 'startDate' or other date fields
  Object.keys(obj).forEach(key => {
    const value = obj[key];

    // Parse known date fields that use LocalDate scalar
    if ((key === 'startDate') && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parts = value.split('-').map(p => parseInt(p, 10));
      obj[key] = new Date(parts[0], parts[1] - 1, parts[2]);
    }
    // Recursively process nested objects and arrays
    else if (value && typeof value === 'object') {
      parseLocalDates(value);
    }
  });
}

// Custom link to parse LocalDate scalars from string to Date
const parseDatesLink = new ApolloLink((operation, forward) => {
  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      next: (result: FetchResult) => {
        if (result.data) {
          parseLocalDates(result.data);
        }
        observer.next(result);
      },
      error: (error: any) => observer.error(error),
      complete: () => observer.complete()
    });

    return () => subscription.unsubscribe();
  });
});

// Mutations that must remain allowed even while saving is locked, so an operator can turn the
// CMT back on (disable maintenance mode) or trigger a process from the System page.
const LOCK_EXEMPT_MUTATIONS = new Set(['SetMaintenanceMode', 'TriggerProcess']);

/**
 * App-level backstop that blocks GraphQL mutations while saving is locked (a publish is running or
 * maintenance mode is on). Queries always pass through, and the System page's own control mutations
 * are exempt so the lock can be lifted. Resolves {@link SystemLockService} lazily to avoid the
 * circular DI it would create (the service itself depends on Apollo).
 */
function createLockLink(injector: Injector): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const isMutation =
      definition.kind === 'OperationDefinition' && definition.operation === 'mutation';

    const isExempt = !!operation.operationName && LOCK_EXEMPT_MUTATIONS.has(operation.operationName);

    if (isMutation && !isExempt) {
      const lock = injector.get(SystemLockService);
      if (lock.isLocked()) {
        return new Observable<FetchResult>((observer) => {
          observer.error(new Error(lock.lockReason() ?? 'Changes are temporarily disabled.'));
        });
      }
    }

    return forward(operation);
  });
}

export function apolloOptionsFactory(): ApolloClientOptions {
  const httpLink = inject(HttpLink);
  const environmentService = inject(EnvironmentService);
  const injector = inject(Injector);

  // Use environment.apiConfig.uri directly - it already includes /graphql
  const uri = environment.apiConfig.uri;

  // One HTTP POST per operation. We previously tried HttpBatchLink (array-batched POST bodies) to cut
  // round-trips, but HotChocolate 16 disables HTTP *request* batching by default, and even with it
  // enabled the batched response is multipart/mixed (requestIndex parts), not the JSON array that
  // Apollo's BatchHttpLink expects — the two are wire-incompatible, so batching failed with HC0009
  // "Invalid GraphQL Request" the moment two operations landed in the same window. Operations already
  // fire in parallel over HTTP, so individual POSTs are the correct terminating link here.
  const httpLinkHandler = httpLink.create({ uri });

  return {
    link: ApolloLink.from([createLockLink(injector), parseDatesLink, httpLinkHandler]),

    cache: new InMemoryCache({
      fragments: fragmentRegistry,
    }),

    // No-cache is CMT's default read methodology. CMT is a concurrent multi-user editor and has NO
    // cache-reactive reads (zero watchQuery consumers) -- every read is a one-shot apollo.query, and
    // every post-mutation refresh is driven by the component re-reading or by the RxJS change
    // Subjects, never by the Apollo cache repainting a view. A shared read cache therefore buys
    // nothing here and actively risks showing one user data that another user has already changed,
    // so queries go to the network by default. Callers that want caching opt in explicitly with
    // fetchPolicy 'cache-first' (the static-lookup allowlist: data read constantly, changed rarely,
    // where brief staleness is harmless -- e.g. cfd-api statuses, file categories). This mirrors the
    // default in DataService.getListNoWatch. watchQuery is left at its default (no callers today).
    defaultOptions: {
      query: { fetchPolicy: 'no-cache' },
    },
  };
}

export const graphqlProvider: ApplicationConfig['providers'] = [
  Apollo,
  {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
  },
];
